/** Home: the setup checklist until the barangay is live, then what needs action today (FR-RPT-01). */
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, MessageSquare, Star, UserCheck, HandHeart, ListChecks, PackageOpen, Tent, Waves, ClipboardCheck, HardHat, Package, Wrench, BookMarked, CalendarDays, UsersRound, Landmark, PiggyBank, Receipt, Ticket, Wallet, Megaphone, PhoneCall, ShieldCheck, Accessibility, Gavel, Hourglass, NotebookPen, Activity, AlarmClock, Baby, BadgeCheck, CalendarClock, CheckCircle2, Circle, Coins, FileCheck2, FileClock, FileSignature, FileText, HeartHandshake, Home, Shield, Stamp, Store, TrendingUp, Users, Vote } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Skeleton } from '../components/ui';
import { useBarangay, type BarangayView } from './setup/BarangayPage';

const STEPS: { key: string; title: string; to: string; hint: string }[] = [
  { key: 'profile', title: 'Barangay profile and logos', to: '/setup/barangay', hint: 'Name, city or municipality, hall address, the barangay seal and the city logo.' },
  { key: 'puroks', title: 'Puroks and sitios', to: '/setup/barangay', hint: 'Used for addresses, hazard zones and reports.' },
  { key: 'officials', title: 'Officials and staff', to: '/setup/officials', hint: 'Punong Barangay, Kagawads, Secretary, Treasurer, SK, tanods, Lupon. Mark the signatories.' },
  { key: 'users', title: 'Users and restricted access', to: '/setup/users', hint: 'Give each staff member a login and role. The Punong Barangay gives VAW Desk and BADAC access.' },
];

interface Tile { key: string; label: string; value: number | string; hint?: string; tone?: 'ok' | 'warning' | 'critical' | 'info'; to?: string }
interface Chart { key: string; title: string; type: 'bar' | 'pie' | 'pyramid' | 'line' | 'hbar'; series: { label: string; value: number; value2?: number }[] }
interface Section { key: string; title: string; tiles: Tile[]; charts?: Chart[]; list?: { title: string; rows: { label: string; detail?: string; to?: string; tone?: 'warning' | 'critical' }[] } }
interface Dashboard { today: string; sections: Section[] }

/** Status colours, RGB standard: red needs action now, amber soon, green is fine, blue is information. */
const STATUS = {
  critical: { ring: 'ring-red-500/30', bg: 'bg-gradient-to-br from-red-500 to-rose-600', soft: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-300' },
  warning: { ring: 'ring-amber-500/30', bg: 'bg-gradient-to-br from-amber-400 to-orange-500', soft: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300' },
  ok: { ring: 'ring-emerald-500/30', bg: 'bg-gradient-to-br from-emerald-500 to-green-600', soft: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300' },
  info: { ring: 'ring-blue-500/30', bg: 'bg-gradient-to-br from-blue-500 to-indigo-600', soft: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-300' },
} as const;
/** Section accents: each module keeps one colour family. */
const ACCENT: Record<string, { from: string; icon: LucideIcon; tone: keyof typeof STATUS }> = {
  documents: { from: 'from-blue-600 to-indigo-600', icon: FileText, tone: 'info' },
  population: { from: 'from-emerald-600 to-teal-600', icon: Users, tone: 'ok' },
  businesses: { from: 'from-violet-600 to-fuchsia-600', icon: Store, tone: 'info' },
  peace: { from: 'from-rose-600 to-red-600', icon: Shield, tone: 'critical' },
  portal: { from: 'from-indigo-600 to-violet-600', icon: Globe, tone: 'info' },
  drrm: { from: 'from-red-600 to-orange-600', icon: Waves, tone: 'critical' },
  social: { from: 'from-teal-600 to-cyan-600', icon: HandHeart, tone: 'ok' },
  legislation: { from: 'from-sky-600 to-blue-700', icon: BookMarked, tone: 'info' },
  projects: { from: 'from-orange-500 to-amber-600', icon: HardHat, tone: 'warning' },
  property: { from: 'from-slate-600 to-slate-800', icon: Package, tone: 'info' },
  treasury: { from: 'from-emerald-600 to-lime-600', icon: Landmark, tone: 'ok' },
  vaw: { from: 'from-pink-600 to-rose-700', icon: HeartHandshake, tone: 'critical' },
  badac: { from: 'from-amber-600 to-orange-700', icon: ShieldCheck, tone: 'warning' },
};
const TILE_ICON: Record<string, LucideIcon> = {
  'docs.forApproval': FileSignature, 'docs.approved': Stamp, 'docs.received': FileClock, 'docs.overdue': AlarmClock, 'docs.released': FileCheck2, 'docs.collected': Coins,
  'pop.inhabitants': Users, 'pop.households': Home, 'pop.seniors': HeartHandshake, 'pop.minors': Baby, 'pop.pwd': Accessibility, 'pop.voters': Vote, 'pop.month': TrendingUp,
  'biz.active': Store, 'biz.cleared': BadgeCheck, 'biz.due': CalendarClock,
  'vaw.open': HeartHandshake, 'vaw.bpo': ShieldCheck, 'vaw.expiring': Hourglass, 'vaw.followups': PhoneCall,
  'badac.report': FileClock, 'badac.meetings': Users, 'badac.activities': Megaphone,
  'fin.today': Receipt, 'fin.month': Coins, 'fin.undeposited': PiggyBank, 'fin.dv': FileSignature, 'fin.budget': Wallet, 'fin.ors': Ticket, 'fin.advances': AlarmClock,
  'leg.sessions': CalendarDays, 'leg.minutes': FileClock, 'leg.enacted': BookMarked, 'leg.assembly': UsersRound,
  'prj.ongoing': HardHat, 'prj.behind': AlarmClock, 'prp.count': Package, 'prp.repair': Wrench, 'prp.countdue': ClipboardCheck,
  'drrm.evacuees': Tent, 'drrm.full': AlarmClock, 'drrm.atrisk': Waves, 'drrm.capacity': Tent, 'drrm.fund': Wallet,
  'soc.beneficiaries': ListChecks, 'soc.month': HandHeart, 'soc.year': Coins, 'soc.dist': PackageOpen,
  'por.pending': UserCheck, 'por.online': Globe, 'por.feedback': MessageSquare, 'por.satisfied': Star,
  'kp.open': Gavel, 'kp.due': Hourglass, 'kp.overdue': AlarmClock, 'blt.month': NotebookPen,
};
const PALETTE = ['#2563EB', '#16A34A', '#DC2626', '#F59E0B', '#7C3AED', '#0891B2', '#DB2777', '#65A30D', '#EA580C', '#4F46E5', '#0D9488'];
const PIE_BY_LABEL: Record<string, string> = { Male: '#2563EB', Female: '#DB2777', Released: '#16A34A', 'For approval': '#2563EB', Approved: '#F59E0B', Received: '#94A3B8', Cancelled: '#DC2626', 'In process': '#F59E0B', 'Not started': '#DC2626' };

function TileCard({ t, accent }: { t: Tile; accent: keyof typeof STATUS }): JSX.Element {
  const tone = STATUS[t.tone ?? accent];
  const Icon = TILE_ICON[t.key] ?? Activity;
  const body = (
    <div className={`group relative h-full overflow-hidden rounded-2xl bg-white p-4 shadow-sm ring-1 ${tone.ring} transition hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-900`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t.label}</p>
          <p className={`mt-1 text-3xl font-bold tabular-nums tracking-tight ${t.tone === 'critical' && Number(t.value) > 0 ? tone.text : 'text-slate-900 dark:text-white'}`}>{typeof t.value === 'number' ? t.value.toLocaleString() : t.value}</p>
        </div>
        <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white shadow ${tone.bg}`}><Icon aria-hidden="true" className="h-5 w-5" /></span>
      </div>
      {t.hint && <p className={`mt-2 rounded-lg px-2 py-1 text-xs ${tone.soft} ${tone.text}`}>{t.hint}</p>}
    </div>
  );
  return t.to ? <Link to={t.to} className="block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand">{body}</Link> : body;
}

function ChartCard({ c }: { c: Chart }): JSX.Element | null {
  if (!c.series.length || c.series.every((x) => !x.value && !x.value2)) return null;
  const total = c.series.reduce((n, x) => n + x.value, 0);
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
      <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">{c.title}</p>
      <div className={c.type === 'pyramid' ? 'h-80' : c.type === 'hbar' ? 'h-64' : 'h-60'}>
        <ResponsiveContainer width="100%" height="100%">
          {c.type === 'bar' ? (
            <BarChart data={c.series} margin={{ left: -20, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" name="Count" radius={[8, 8, 0, 0]}>{c.series.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
            </BarChart>
          ) : c.type === 'hbar' ? (
            <BarChart data={c.series} layout="vertical" margin={{ left: 30, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" name="Count" radius={[0, 8, 8, 0]}>{c.series.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
            </BarChart>
          ) : c.type === 'pyramid' ? (
            <BarChart data={c.series.map((x) => ({ label: x.label, Male: -x.value, Female: x.value2 ?? 0 }))} layout="vertical" stackOffset="sign" margin={{ left: 0, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => String(Math.abs(v))} allowDecimals={false} />
              <YAxis type="category" dataKey="label" width={48} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: number) => Math.abs(v)} />
              <Legend />
              <Bar dataKey="Male" stackId="p" fill="#2563EB" radius={[8, 0, 0, 8]} />
              <Bar dataKey="Female" stackId="p" fill="#DB2777" radius={[0, 8, 8, 0]} />
            </BarChart>
          ) : c.type === 'line' ? (
            <AreaChart data={c.series.map((x) => ({ label: x.label, Requests: x.value, Released: x.value2 ?? 0 }))} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="gReq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
                <linearGradient id="gRel" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16A34A" stopOpacity={0.35} /><stop offset="100%" stopColor="#16A34A" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="Requests" stroke="#2563EB" strokeWidth={2} fill="url(#gReq)" />
              <Area type="monotone" dataKey="Released" stroke="#16A34A" strokeWidth={2} fill="url(#gRel)" />
            </AreaChart>
          ) : (
            <PieChart>
              <Pie data={c.series} dataKey="value" nameKey="label" innerRadius={55} outerRadius={85} paddingAngle={2} isAnimationActive={false} label={({ value }: { value: number }) => value}>
                {c.series.map((x, i) => <Cell key={x.label} fill={PIE_BY_LABEL[x.label] ?? PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip formatter={(v: number) => `${v} (${total ? Math.round((v / total) * 100) : 0}%)`} />
              <Legend />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Sections({ data }: { data: Dashboard }): JSX.Element {
  if (!data.sections.length) return <Alert tone="info">Nothing to show for your role yet.</Alert>;
  return (
    <div className="space-y-10">
      {data.sections.map((s) => {
        const accent = ACCENT[s.key] ?? { from: 'from-slate-600 to-slate-800', icon: Activity, tone: 'info' as const };
        const Icon = accent.icon;
        return (
          <section key={s.key} aria-labelledby={`sec-${s.key}`}>
            <div className="mb-4 flex items-center gap-3">
              <span className={`grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br ${accent.from} text-white shadow`}><Icon aria-hidden="true" className="h-5 w-5" /></span>
              <h2 id={`sec-${s.key}`} className="text-lg font-bold text-slate-900 dark:text-white">{s.title}</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{s.tiles.map((t) => <TileCard key={t.key} t={t} accent={accent.tone} />)}</div>
            {((s.charts?.length ?? 0) > 0 || (s.list?.rows.length ?? 0) > 0) && (
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                {s.charts?.map((c) => <ChartCard key={c.key} c={c} />)}
                {s.list && s.list.rows.length > 0 && (
                  <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                    <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">{s.list.title}</p>
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                      {s.list.rows.map((r, i) => (
                        <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                          <span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${r.tone === 'critical' ? 'bg-red-500' : r.tone === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />{r.to ? <Link className="font-medium hover:underline" to={r.to}>{r.label}</Link> : <span className="font-medium">{r.label}</span>}</span>
                          <span className={`rounded-full px-2 py-0.5 text-xs ${r.tone === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' : r.tone === 'warning' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>{r.detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const barangay = useBarangay();
  const dash = useQuery({ queryKey: ['dashboard'], queryFn: () => api<Dashboard>('/dashboard'), enabled: Boolean(user?.companyId), refetchInterval: 60_000 });
  const mark = useMutation({
    mutationFn: (step: string) => api<BarangayView>('/barangay/setup/complete-step', { method: 'POST', body: { step } }),
    onSuccess: (data) => qc.setQueryData(['barangay'], data),
  });
  if (!user?.companyId) {
    return <Alert tone="info">{user?.isPlatformAdmin ? 'You are signed in as an ITCSI platform administrator. Open Barangays to onboard one.' : 'Your login is not linked to a barangay yet. Ask your administrator.'}</Alert>;
  }
  if (barangay.isLoading || !barangay.data) return <Skeleton className="h-64 w-full" />;
  const b = barangay.data;
  const done = new Set(b.setupSteps);
  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-blue-700 via-indigo-700 to-emerald-600 p-6 text-white shadow-lg">
        <p className="text-sm font-medium text-white/80">{new Date().toLocaleDateString('en-PH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Asia/Manila' })}</p>
        <h1 className="mt-1 text-2xl font-bold sm:text-3xl">{b.name}</h1>
        <p className="text-sm text-white/80">{[b.cityMunicipality, b.province].filter(Boolean).join(', ') || 'Barangay'} · Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-3 py-1"><span className="h-2 w-2 rounded-full bg-white" />Red: act now</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/90 px-3 py-1 text-amber-950"><span className="h-2 w-2 rounded-full bg-amber-950" />Amber: soon</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/90 px-3 py-1"><span className="h-2 w-2 rounded-full bg-white" />Green: on track</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/90 px-3 py-1"><span className="h-2 w-2 rounded-full bg-white" />Blue: information</span>
        </div>
      </div>
      {!b.setupComplete && (
        <Card title="Set up your barangay" description="Finish these and the barangay goes live. Residents, certificates and the other modules follow.">
          <ol className="divide-y divide-slate-100 dark:divide-slate-800">
            {STEPS.map((s) => (
              <li key={s.key} className="flex items-start gap-3 py-3">
                {done.has(s.key) ? <CheckCircle2 aria-hidden="true" className="mt-0.5 h-5 w-5 text-emerald-600" /> : <Circle aria-hidden="true" className="mt-0.5 h-5 w-5 text-slate-300" />}
                <div className="min-w-0 flex-1">
                  <Link to={s.to} className="font-medium text-slate-900 hover:underline dark:text-white">{s.title}</Link>
                  <p className="text-sm text-slate-500">{s.hint}</p>
                </div>
                {!done.has(s.key) && can('barangay.manage') && <Button size="sm" variant="secondary" busy={mark.isPending && mark.variables === s.key} onClick={() => mark.mutate(s.key)}>Mark done</Button>}
              </li>
            ))}
          </ol>
        </Card>
      )}
      <div className={b.setupComplete ? '' : 'mt-8'}>
        {dash.isLoading ? <Skeleton className="h-64 w-full" /> : dash.data ? <Sections data={dash.data} /> : null}
      </div>
    </div>
  );
}
